import { useMemo } from 'react';
import * as R from 'remeda';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { BillingStatsValues } from '@rossum/api-client/billing';
import { IconFile } from '@rossum/ui/icons/tabler';
import { MenuItem, Paper, Stack, SvgIcon, useTheme } from '@rossum/ui/material';
import { BarChart } from '@rossum/ui/x-charts-pro';
import TextFieldControl from '../../../../components/ReactHookForm/controls/TextFieldControl';
import {
  Aggregate,
  AggregateData,
} from '../../../../containers/Statistics/containers/Aggregates';
import { LocalizationKeys } from '../../../../i18n';
import { formatNumberToLocale } from '../../../../lib/helpers';
import { dateFormatByGranularity } from '../../../../lib/timeUtils';
import { getTemporaryDataForChart, getValuesByUnit } from '../../helpers';
import { BillingEntity } from '../../hooks/useBillingEntity';
import {
  PageStatisticsGranularity,
  usePageStatisticsTimeline,
} from '../../hooks/usePageStatisticsTimeline';
import { LoadingOverlay } from '../LoadingOverlay';
import { StatisticsFilterFormState } from '../StatisticsFilter';
import { EmptyChart } from './EmptyChart';

const CHART_HEIGHT = 400;
const BAR_CHART_HEIGHT = 250;
// Non-billable bar uses a muted color from the grey palette
const NON_BILLABLE_COLOR = '#616161'; // grey[700]

const granularityOptions: Array<{
  value: PageStatisticsGranularity;
  labelId: LocalizationKeys;
}> = [
  {
    value: 'month',
    labelId: 'containers.statistics.granularities.month',
  },
  {
    value: 'week',
    labelId: 'containers.statistics.granularities.week',
  },
];

type GranularityFormState = {
  granularity: PageStatisticsGranularity;
};

type BillingBarChartProps = {
  billingEntity: BillingEntity;
  filters: StatisticsFilterFormState;
};

const BillingBarChart = ({ billingEntity, filters }: BillingBarChartProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const { control } = useForm<GranularityFormState>({
    defaultValues: { granularity: 'week' },
  });

  const granularity = useWatch({ control, name: 'granularity' });
  const { data, isLoading } = usePageStatisticsTimeline(
    billingEntity,
    R.omit(filters, ['unit']),
    granularity
  );

  const isTmpState = isLoading && !data;
  const temporaryGraphData = useMemo(() => getTemporaryDataForChart(), []);

  const getAggregateData = (
    totals: Partial<BillingStatsValues>
  ): AggregateData => {
    const { billable, nonBillable } = getValuesByUnit(totals, filters.unit);
    return [
      {
        value: formatNumberToLocale(billable, intl.locale),
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'inherit' : 'info'}>
            <IconFile />
          </SvgIcon>
        ),
        description: 'components.graphs.billing.series.billable',
      },
      {
        value: formatNumberToLocale(nonBillable, intl.locale),
        icon: (
          <SvgIcon fontSize="large">
            <IconFile />
          </SvgIcon>
        ),
        description: 'components.graphs.billing.series.nonBillable',
      },
    ];
  };

  const chartData = (data ?? temporaryGraphData).results.map(
    ({ beginDate, endDate, values }) => {
      const { billable, nonBillable } = getValuesByUnit(values, filters.unit);
      return {
        billable,
        nonBillable,
        date: dateFormatByGranularity(
          intl.locale,
          granularity,
          beginDate,
          endDate
        ),
      };
    }
  );

  return (
    <Stack
      component={Paper}
      position="relative"
      justifyContent="flex-start"
      height={CHART_HEIGHT}
      p={2}
      width="100%"
    >
      {isTmpState && <LoadingOverlay color="info" />}
      {isLoading || data?.results.length ? (
        <>
          <Stack direction="row" justifyContent="space-between">
            <Aggregate
              data={getAggregateData((data ?? temporaryGraphData).totals)}
            />
            <TextFieldControl
              ControllerProps={{ control, name: 'granularity' }}
              select
            >
              {granularityOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {intl.formatMessage({ id: option.labelId })}
                </MenuItem>
              ))}
            </TextFieldControl>
          </Stack>
          <BarChart
            skipAnimation
            height={BAR_CHART_HEIGHT}
            series={[
              {
                data: chartData.map(d => d.billable),
                stack: 'total',
                color: isTmpState
                  ? theme.palette.text.disabled
                  : theme.palette.info.main,
                label: intl.formatMessage({
                  id: 'components.graphs.billing.series.billable',
                }),
              },
              {
                data: chartData.map(d => d.nonBillable),
                stack: 'total',
                color: NON_BILLABLE_COLOR,
                label: intl.formatMessage({
                  id: 'components.graphs.billing.series.nonBillable',
                }),
              },
            ]}
            xAxis={[
              {
                data: chartData.map(d => d.date),
                scaleType: 'band',
              },
            ]}
            yAxis={[
              {
                min: 0,
                max: chartData.every(
                  d => d.billable === 0 && d.nonBillable === 0
                )
                  ? 1
                  : undefined,
              },
            ]}
            grid={{ horizontal: true }}
            hideLegend
          />
        </>
      ) : (
        <EmptyChart
          title="containers.billing.emptyGraph"
          text="containers.billing.emptyDescription"
        />
      )}
    </Stack>
  );
};

export { BillingBarChart };
